import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Copy, Pencil, Plus, Shield, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { playsRole } from "../utils/championRoles";
import { championList,getChampionIconUrl, getChampionName, getChampionSplashUrl } from "../utils/ddragon";
import {
	activeSet,
	addSet,
	deleteSet,
	duplicateSet,
	emptyDraft,
	loadBook,
	moveChampion,
	renameSet,
	saveBook,
	selectSet,
	SIDES,
	SLOT_LABEL,
	SLOTS,
	toggleChampion,
	updateActive,
	type DraftBook,
	type DraftPrep,
	type Side,
	type Slot
} from "../utils/draftPrep";
import { button, label } from "./ui/primitives";

const chip = "flex h-5 w-5 items-center justify-center rounded bg-black/60 text-ink-2 hover:bg-black/85 hover:text-ink disabled:opacity-30 cursor-pointer disabled:cursor-default";
const SIDE: Record<Side, { title: string; sub: string; text: string; bar: string }> = {
	ours: { title: "Our team", sub: "blue side", text: "text-side-blue", bar: "border-t-side-blue" },
	theirs: { title: "Enemy", sub: "red side", text: "text-side-red", bar: "border-t-side-red" }
};
const PICK_SLOTS = SLOTS.filter(s => s !== "BANS");

interface Target {
	side: Side;
	slot: Slot;
}

const pickCount = (d: DraftPrep) => SIDES.reduce((n, side) => n + SLOTS.reduce((m, slot) => m + d[side][slot].length, 0), 0);
const iconButton = cn(button.secondary, "h-8 w-8 px-0");

/** One tab per draft set, plus "New". Double-click (or the pencil in the header) renames the active one inline. */
function SetTabs({ book, renaming, onSelect, onAdd, onRenaming, onRename }: { book: DraftBook; renaming: boolean; onSelect: (id: string) => void; onAdd: () => void; onRenaming: (on: boolean) => void; onRename: (name: string) => void }) {
	return (
		<div role="tablist" aria-label="Draft sets" className="mb-3 flex items-center gap-1 overflow-x-auto border-b border-line">
			{book.sets.map(set => {
				const on = set.id === book.activeId;
				if (on && renaming)
					return (
						<input
							key={set.id}
							autoFocus
							defaultValue={set.name}
							maxLength={40}
							aria-label="Draft set name"
							onFocus={e => e.target.select()}
							// Escape marks the input before blurring, so the blur handler is the single place that ends renaming
							onBlur={e => {
								if (!e.target.dataset.cancel) onRename(e.target.value);
								onRenaming(false);
							}}
							onKeyDown={e => {
								if (e.key === "Escape") e.currentTarget.dataset.cancel = "1";
								if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
							}}
							className="my-1 h-8 w-44 shrink-0 rounded-md border border-signal bg-sunken px-2 text-[13px] text-ink"
						/>
					);
				return (
					<button
						key={set.id}
						type="button"
						role="tab"
						aria-selected={on}
						onClick={() => onSelect(set.id)}
						onDoubleClick={() => on && onRenaming(true)}
						className={cn("-mb-px flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-[13px]", on ? "border-signal font-bold text-ink" : "border-transparent text-ink-2 hover:text-ink")}
					>
						{set.name}
						<span className="font-mono text-[10px] font-normal text-ink-3">{pickCount(set.draft)}</span>
					</button>
				);
			})}
			<button type="button" onClick={onAdd} className="flex shrink-0 cursor-pointer items-center gap-1 px-3 py-2 text-[13px] font-semibold text-signal hover:text-signal-hover">
				<Plus size={14} aria-hidden="true" />
				New
			</button>
		</div>
	);
}

// Splash art keeps the face in the upper part, about 10-40% from the top and right of centre. A wide bar only shows ~40% of the
// image height, so the crop is anchored near the top (10%) instead of the middle.
/** One champion in a slot: wallpaper tile with name and priority controls. Several share the width of a role bar. */
function Tile({ id, first, last, ban, onMove, onRemove }: { id: number; first: boolean; last: boolean; ban?: boolean; onMove: (delta: -1 | 1) => void; onRemove: () => void }) {
	const name = getChampionName(id);
	return (
		<li title={name} className={cn("group relative min-w-0 overflow-hidden rounded-sm bg-sunken bg-cover bg-[position:58%_10%]", ban ? "h-16 w-16 shrink-0" : "h-full flex-1")} style={ban ? undefined : { backgroundImage: `url(${getChampionSplashUrl(id)})` }}>
			{/* bans show the plain icon; pick tiles use the wallpaper, so keep a tiny img for screen readers / broken art */}
			<img src={getChampionIconUrl(id)} alt="" className={ban ? "h-full w-full object-cover" : "sr-only"} />
			{!ban && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />}
			<div className="absolute right-0.5 top-0.5 flex gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
				{!ban && <button type="button" aria-label={`Move ${name} earlier`} disabled={first} onClick={onMove.bind(null, -1)} className={chip}>
					<ChevronLeft size={12} aria-hidden="true" />
				</button>}
				{!ban && <button type="button" aria-label={`Move ${name} later`} disabled={last} onClick={onMove.bind(null, 1)} className={chip}>
					<ChevronRight size={12} aria-hidden="true" />
				</button>}
				<button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className={chip}>
					<X size={12} aria-hidden="true" />
				</button>
			</div>
			{!ban && <span className="absolute inset-x-1.5 bottom-1 truncate text-[11px] font-bold text-white drop-shadow">{name}</span>}
		</li>
	);
}

/** A role bar (or the ban strip): click anywhere empty to make it the slot the champion grid adds to. */
function SlotRow({ side, slot, draft, active, onActivate, onChange }: { side: Side; slot: Slot; draft: DraftPrep; active: boolean; onActivate: () => void; onChange: (d: DraftPrep) => void }) {
	const ids = draft[side][slot];
	const ban = slot === "BANS";
	return (
		// onClick on the row so clicks on tiles bubble up; the button below stays as the keyboard/screen-reader target
		<div onClick={onActivate} className={cn("relative flex cursor-pointer gap-1.5", ban ? "flex-wrap gap-2" : cn("h-[120px] bg-surface p-1.5", side === "ours" ? "border-l-[3px] border-l-side-blue" : "border-r-[3px] border-r-side-red", active && "ring-2 ring-inset ring-signal"), ban && side === "theirs" && "justify-end")}>
			<button type="button" aria-pressed={active} onClick={onActivate} className="absolute inset-0 z-0 cursor-pointer" aria-label={`Add to ${SIDE[side].title} ${SLOT_LABEL[slot]}`} />
			{!ids.length && !ban && (
				<span className={cn(label, "pointer-events-none z-10 m-auto flex flex-col items-center gap-1.5", active && "text-signal")}>
					<Shield size={26} aria-hidden="true" className="opacity-40" />
					{SLOT_LABEL[slot]}
				</span>
			)}
			{ids.length > 0 && (
				<ol className={cn("relative z-10 m-0 flex list-none p-0", ban ? "flex-wrap gap-2" : "h-full w-full gap-1.5", ban && side === "theirs" && "justify-end")}>
					{ids.map((id, i) => (
						<Tile key={id} id={id} ban={ban} first={i === 0} last={i === ids.length - 1} onMove={d => onChange(moveChampion(draft, side, slot, i, d))} onRemove={() => onChange(toggleChampion(draft, side, slot, id))} />
					))}
				</ol>
			)}
			{ban &&
				Array.from({ length: Math.max(0, 5 - ids.length) }, (_, i) => (
					<span key={i} aria-hidden="true" className={cn("pointer-events-none z-10 flex h-16 w-16 items-center justify-center rounded-sm border bg-surface text-ink-3", active ? "border-signal/60" : "border-line")}>
						<Shield size={20} className="opacity-40" />
					</span>
				))}
			{ids.length > 0 && !ban && (
				<span className={cn(label, "pointer-events-none absolute left-3 top-2.5 z-20 text-white drop-shadow", active && "text-signal")}>{SLOT_LABEL[slot]}</span>
			)}
		</div>
	);
}

function SidePanel({ side, draft, active, onActivate, onChange, className }: { side: Side; draft: DraftPrep; active: Target; onActivate: (t: Target) => void; onChange: (d: DraftPrep) => void; className?: string }) {
	const row = (slot: Slot) => <SlotRow key={slot} side={side} slot={slot} draft={draft} active={active.side === side && active.slot === slot} onActivate={() => onActivate({ side, slot })} onChange={onChange} />;
	return (
		<div className={cn("flex min-w-0 flex-col gap-4", className)}>
			<section className="flex flex-col gap-1.5">
				<h2 className={cn("m-0 mb-1 flex items-baseline gap-2", side === "theirs" && "flex-row-reverse")}>
					<span className={cn("text-xl font-bold", SIDE[side].text)}>{SIDE[side].title}</span>
					<span className="text-xs text-ink-3">{SIDE[side].sub}</span>
				</h2>
				{PICK_SLOTS.map(row)}
			</section>
			<section className="flex flex-col gap-1.5">
				<h2 className={cn(label, "m-0", side === "theirs" && "text-right", active.side === side && active.slot === "BANS" && "text-signal")}>{side === "ours" ? "Our bans" : "Enemy bans"}</h2>
				{row("BANS")}
			</section>
		</div>
	);
}

export function DraftPage() {
	const [book, setBook] = useState(loadBook);
	const [active, setActive] = useState<Target>({ side: "ours", slot: "TOP" });
	const [query, setQuery] = useState("");
	const [role, setRole] = useState<Slot | "ALL">("ALL");
	const [confirm, setConfirm] = useState<"clear" | "delete" | null>(null);
	const [renaming, setRenaming] = useState(false);

	useEffect(() => saveBook(book), [book]);

	const current = activeSet(book);
	const draft = current.draft;
	const setDraft = (next: DraftPrep | ((d: DraftPrep) => DraftPrep)) => setBook(b => updateActive(b, typeof next === "function" ? next(activeSet(b).draft) : next));
	// switching sets starts the new one fresh: our Top slot, no leftover search
	const switchTo = (change: (b: DraftBook) => DraftBook) => {
		setBook(change);
		setActive({ side: "ours", slot: "TOP" });
		setQuery("");
		setRenaming(false);
	};
	// two-click confirm for destructive actions; blur cancels it
	const confirmed = (kind: "clear" | "delete", run: () => void) => () => {
		if (confirm !== kind) return setConfirm(kind);
		run();
		setConfirm(null);
	};

	const q = query.trim().toLowerCase();
	const shown = championList.filter(c => (!q || c.name.toLowerCase().includes(q)) && (role === "ALL" || playsRole(c.id, role)));
	const selected = draft[active.side][active.slot];
	const toggle = (id: number) => setDraft(d => toggleChampion(d, active.side, active.slot, id));

	return (
		<div className="pt-6">
			<div className="mb-4 flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="m-0 text-xl font-extrabold">Draft prep</h1>
					<p className="m-0 mt-1 text-[13px] text-ink-2">Pick a role, then add champions in priority order. Keep one set per opponent or plan. Kept in this browser only.</p>
				</div>
				<div className="flex gap-1.5">
					<button type="button" title="Rename" aria-label={`Rename ${current.name}`} className={iconButton} onClick={() => setRenaming(true)}>
						<Pencil size={14} aria-hidden="true" />
					</button>
					<button type="button" title="Duplicate" aria-label={`Duplicate ${current.name}`} className={iconButton} onClick={() => switchTo(b => duplicateSet(b, current.id))}>
						<Copy size={14} aria-hidden="true" />
					</button>
					<button type="button" className={cn(button.secondary, "h-8 px-3 text-xs")} onClick={confirmed("clear", () => setDraft(emptyDraft()))} onBlur={() => setConfirm(null)}>
						{confirm === "clear" ? "Click again to clear" : "Clear"}
					</button>
					{/* the last set can only be cleared, so there is always a board to work on */}
					{book.sets.length > 1 && (
						<button
							type="button"
							title="Delete set"
							aria-label={confirm === "delete" ? `Click again to delete ${current.name}` : `Delete ${current.name}`}
							className={cn(button.secondary, "h-8 text-xs", confirm === "delete" ? "px-3" : "w-8 px-0")}
							onClick={confirmed("delete", () => switchTo(b => deleteSet(b, current.id)))}
							onBlur={() => setConfirm(null)}
						>
							{confirm === "delete" ? `Click again to delete “${current.name}”` : <Trash2 size={14} aria-hidden="true" />}
						</button>
					)}
				</div>
			</div>

			<SetTabs
				book={book}
				renaming={renaming}
				onSelect={id => id !== book.activeId && switchTo(b => selectSet(b, id))}
				onAdd={() => switchTo(addSet)}
				onRenaming={setRenaming}
				onRename={name => setBook(b => renameSet(b, current.id, name))}
			/>

			<div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1.15fr)]">
				<SidePanel side="ours" draft={draft} active={active} onActivate={setActive} onChange={setDraft} />
				<SidePanel side="theirs" draft={draft} active={active} onActivate={setActive} onChange={setDraft} className="lg:order-3" />

				<section className="relative lg:order-2">
					{/* on desktop the card is taken out of flow so only the team columns set the row height */}
					<div className="flex flex-col lg:absolute lg:inset-0 lg:px-3">
					{/* Enter adds the first match, so a whole pool can be typed without the mouse */}
					<form
						className="mb-3 flex flex-col gap-2"
						onSubmit={e => {
							e.preventDefault();
							if (!q || !shown[0]) return;
							toggle(shown[0].id);
							setQuery("");
						}}
					>
						<span className="text-[13px] text-ink-2">
							Adding to <span className={cn("font-bold", SIDE[active.side].text)}>{SIDE[active.side].title}</span> · <span className="font-bold text-ink">{SLOT_LABEL[active.slot]}</span>
						</span>
						<input
							type="search"
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder="Search champion"
							aria-label="Search champion"
							className="h-9 w-full rounded-lg border border-line-2 bg-sunken px-3 text-[13px] text-ink placeholder:text-ink-3"
						/>
						<div role="group" aria-label="Filter by role" className="flex flex-wrap gap-1">
							{(["ALL", ...PICK_SLOTS] as const).map(r => (
								<button
									key={r}
									type="button"
									aria-pressed={role === r}
									onClick={() => setRole(r)}
									className={cn("h-7 cursor-pointer rounded-md border px-2.5 text-xs", role === r ? "border-signal bg-raised font-bold text-signal" : "border-line text-ink-2 hover:bg-raised")}
								>
									{r === "ALL" ? "All" : SLOT_LABEL[r]}
								</button>
							))}
						</div>
					</form>
					{shown.length ? (
						<ul className="m-0 grid content-start max-h-[60vh] lg:max-h-none lg:min-h-0 lg:flex-1 list-none grid-cols-[repeat(auto-fill,minmax(60px,1fr))] gap-1 overflow-y-auto p-0.5">
							{shown.map(c => {
								const on = selected.includes(c.id);
								return (
									<li key={c.id}>
										<button
											type="button"
											aria-pressed={on}
											title={c.name}
											onClick={() => toggle(c.id)}
											className={cn("flex w-full cursor-pointer flex-col items-center gap-1 rounded-lg border p-1 hover:bg-raised", on ? "border-signal bg-raised" : "border-transparent")}
										>
											<img src={getChampionIconUrl(c.id)} alt="" loading="lazy" className="aspect-square w-full rounded-[6px]" />
											<span className={cn("w-full truncate text-center text-[10px]", on ? "font-bold text-signal" : "text-ink-2")}>{c.name}</span>
										</button>
									</li>
								);
							})}
						</ul>
					) : (
						<div className="text-[13px] text-ink-3">No champion matches “{query.trim()}”</div>
					)}
					</div>
				</section>
			</div>
		</div>
	);
}
